import type { IsoDateTime, QuestionId } from '../model/brands.ts';
import type { CanonicalExam } from '../model/canonical.ts';
import { isAnswered, type Confidence, type QuestionReport, type Response } from '../model/session.ts';
import { questionAt } from '../model/canonical.ts';
import { applySelfGrade, scoreQuestion } from '../scoring/registry.ts';
import { closeSegment, elapsedMs, hasExpired, openSegment } from './clock.ts';
import {
  currentQuestionId,
  enterQuestion,
  leaveQuestion,
  MAX_NOTE_LENGTH,
  type SessionState,
} from './state.ts';

/**
 * Everything that can happen to a session.
 *
 * `at` is not part of an event: it is passed alongside, because the reducer must be a pure function
 * of state, event and time. Nothing in here reads a clock.
 */
export type SessionEvent =
  | { readonly type: 'NAVIGATE'; readonly to: number }
  | { readonly type: 'NEXT' }
  | { readonly type: 'PREVIOUS' }
  | { readonly type: 'ANSWER'; readonly questionId: QuestionId; readonly response: Response | null }
  | { readonly type: 'TOGGLE_MARK'; readonly questionId: QuestionId }
  | { readonly type: 'SET_CONFIDENCE'; readonly questionId: QuestionId; readonly value: Confidence | null }
  | { readonly type: 'SET_NOTE'; readonly questionId: QuestionId; readonly text: string }
  | { readonly type: 'REVEAL'; readonly questionId: QuestionId }
  | { readonly type: 'SELF_GRADE'; readonly questionId: QuestionId; readonly correct: boolean }
  | { readonly type: 'REPORT'; readonly report: QuestionReport }
  | { readonly type: 'VISIBILITY'; readonly visible: boolean }
  | { readonly type: 'PAUSE' }
  | { readonly type: 'RESUME' }
  | { readonly type: 'TICK' }
  | { readonly type: 'REQUEST_SUBMIT' }
  | { readonly type: 'CANCEL_SUBMIT' }
  | { readonly type: 'CONFIRM_SUBMIT' }
  | { readonly type: 'ENTER_REVIEW' }
  | { readonly type: 'EXIT_REVIEW' };

export interface Now {
  /** Monotonic milliseconds — `performance.now()`. Never a wall clock. */
  readonly monotonic: number;
  /** Wall-clock timestamp, recorded but never used to compute a duration. */
  readonly wall: IsoDateTime;
}

/**
 * The session state machine.
 *
 * Total and pure: an event that does not apply returns the **same object**, never throws and never
 * half-applies. That property is what lets the UI ask `can()` instead of duplicating the rules, and
 * what makes the machine exhaustively testable — every (state, event) pair either changes something
 * or is identity.
 */
export function reduce(
  state: SessionState,
  event: SessionEvent,
  now: Now,
  exam?: CanonicalExam,
): SessionState {
  switch (event.type) {
    case 'NAVIGATE':
      return navigate(state, event.to, now);

    case 'NEXT':
      return navigate(state, state.cursor + 1, now);

    case 'PREVIOUS':
      return state.policy.allowBackNavigation ? navigate(state, state.cursor - 1, now) : state;

    case 'ANSWER':
      return answer(state, event.questionId, event.response, now);

    case 'TOGGLE_MARK': {
      if (!state.policy.allowMarkForReview || !isLive(state)) {
        return state;
      }
      const entry = state.entries[event.questionId];
      if (entry === undefined) {
        return state;
      }
      return withEntry(state, event.questionId, { markedForReview: !entry.markedForReview });
    }

    case 'SET_CONFIDENCE': {
      if (!state.policy.collectConfidence || !isLive(state)) {
        return state;
      }
      return state.entries[event.questionId] === undefined
        ? state
        : withEntry(state, event.questionId, { confidence: event.value });
    }

    case 'SET_NOTE': {
      // Deliberately allowed in every status, including after submitting: half the value of a note
      // is what you add to it once you have seen whether you were right. Notes are the candidate's
      // own words about a question, not part of answering it, so no policy gates them.
      if (state.entries[event.questionId] === undefined) {
        return state;
      }
      const text = event.text.slice(0, MAX_NOTE_LENGTH);
      // Writing the same note again is not a change; returning the same object keeps the reducer's
      // "an event that does nothing changes nothing" property true for the noisiest event there is,
      // since this one fires on every keystroke.
      return state.entries[event.questionId]?.note === text
        ? state
        : withEntry(state, event.questionId, { note: text });
    }

    case 'REVEAL': {
      const entry = state.entries[event.questionId];
      // Deliberately allowed with no answer given. Looking something up is a legitimate way to
      // study, and pretending otherwise only teaches people to guess first to unlock the
      // explanation. The cost is real and automatic: revealing locks the answer, so a question
      // read this way stays unanswered and scores nothing.
      if (!state.policy.immediateFeedback || !isLive(state) || entry?.revealedAt !== null) {
        return state;
      }
      return withEntry(state, event.questionId, { revealedAt: now.wall });
    }

    case 'SELF_GRADE': {
      // Only meaningful once the session is over: it resolves a question the app could not score.
      if (state.status !== 'completed' && state.status !== 'reviewing') {
        return state;
      }
      // Only a question the app could not score, or one the candidate has already graded and is
      // changing their mind about.
      const score = state.scores[event.questionId];
      const alreadyGraded = entryGrade(state, event.questionId) !== null;
      if (score === undefined || (score.outcome !== 'ungraded' && !alreadyGraded)) {
        return state;
      }
      return {
        ...withEntry(state, event.questionId, { selfGrade: event.correct }),
        scores: {
          ...state.scores,
          [event.questionId]: applySelfGrade(
            { ...score, outcome: 'ungraded' },
            event.correct,
          ),
        },
      };
    }

    case 'REPORT': {
      if (state.entries[event.report.questionId] === undefined) {
        return state;
      }
      // One report per question: reporting again replaces it rather than piling up.
      const others = state.reports.filter(
        (report) => report.questionId !== event.report.questionId,
      );
      return { ...state, reports: [...others, event.report] };
    }

    case 'VISIBILITY':
      return visibility(state, event.visible, now);

    case 'PAUSE':
      return state.status === 'active' ? { ...pause(state, now), pausedByUser: true } : state;

    case 'RESUME':
      return state.status === 'paused' ? { ...resume(state, now), pausedByUser: false } : state;

    case 'TICK':
      // The exam goes with it. A tick can end the session, and a session that ended has to be
      // scored — running out of time is not a reason to lose the marks already earned.
      return tick(state, now, exam);

    case 'REQUEST_SUBMIT':
      return isLive(state) ? { ...state, status: 'confirming-submit' } : state;

    case 'CANCEL_SUBMIT': {
      if (state.status !== 'confirming-submit') {
        return state;
      }
      // Back to whatever it was, which is not always "answering": submitting is offered during a
      // pause so nobody is trapped, and cancelling then must not quietly unpause. The clock is the
      // authority — a session whose clock is stopped *is* paused — so there is no second copy of
      // that fact to fall out of step.
      return { ...state, status: clockRunning(state) ? 'active' : 'paused' };
    }

    case 'CONFIRM_SUBMIT':
      return isLive(state) || state.status === 'confirming-submit' ? submit(state, now, exam) : state;

    case 'ENTER_REVIEW':
      return state.status === 'completed' ? { ...state, status: 'reviewing' } : state;

    case 'EXIT_REVIEW':
      return state.status === 'reviewing' ? { ...state, status: 'completed' } : state;
  }
}

/** Live means the candidate can still act on questions. */
function isLive(state: SessionState): boolean {
  return state.status === 'active' || state.status === 'paused';
}

/** Whether time is currently being counted: the last segment is open. */
function clockRunning(state: SessionState): boolean {
  return state.clock.segments[state.clock.segments.length - 1]?.endedAt === null;
}

function entryGrade(state: SessionState, questionId: QuestionId): boolean | null {
  return state.entries[questionId]?.selfGrade ?? null;
}

function withEntry(
  state: SessionState,
  questionId: QuestionId,
  patch: Partial<SessionState['entries'][string]>,
): SessionState {
  const entry = state.entries[questionId];
  if (entry === undefined) {
    return state;
  }
  return { ...state, entries: { ...state.entries, [questionId]: { ...entry, ...patch } } };
}

function navigate(state: SessionState, to: number, now: Now): SessionState {
  if (state.status === 'completed') {
    return state;
  }
  const bounded = Math.max(0, Math.min(to, state.blueprint.questionOrder.length - 1));
  if (bounded === state.cursor || state.blueprint.questionOrder.length === 0) {
    return state;
  }
  if (bounded < state.cursor && !state.policy.allowBackNavigation && state.status !== 'reviewing') {
    return state;
  }

  return enterQuestion(leaveQuestion(state, now.monotonic), bounded, now.monotonic);
}

function answer(
  state: SessionState,
  questionId: QuestionId,
  response: Response | null,
  now: Now,
): SessionState {
  const entry = state.entries[questionId];
  if (entry === undefined || state.status !== 'active') {
    return state;
  }

  // Once feedback has been shown, changing the answer would only be learning the explanation.
  if (entry.revealedAt !== null) {
    return state;
  }
  const alreadyAnswered = isAnswered(entry.response);
  if (alreadyAnswered && !state.policy.allowChangeAnswer) {
    return state;
  }

  return withEntry(state, questionId, {
    response,
    changedAnswerCount: alreadyAnswered ? entry.changedAnswerCount + 1 : entry.changedAnswerCount,
    firstAnsweredAt:
      entry.firstAnsweredAt ?? (isAnswered(response) ? now.wall : null),
  });
}

/**
 * The tab was hidden or shown.
 *
 * Whether the clock stops is a policy decision, and the two modes genuinely differ: practice should
 * not punish someone for looking something up, while a timed exam that pauses whenever the window
 * loses focus is not a timed exam. Either way the count is recorded, because a result that was
 * measured across interruptions should say so.
 */
function visibility(state: SessionState, visible: boolean, now: Now): SessionState {
  if (!isLive(state)) {
    return state;
  }

  if (visible) {
    // Coming back to the tab must not undo a pause the candidate asked for. Only the clock that
    // stopped itself may start itself again.
    if (state.status !== 'paused' || state.pausedByUser) {
      return state;
    }
    return resume(state, now);
  }

  const counted: SessionState = {
    ...state,
    clock: { ...state.clock, visibilityLostCount: state.clock.visibilityLostCount + 1 },
  };

  return state.policy.pauseWhenHidden ? pause(counted, now) : counted;
}

/**
 * Stops the clock — the session one and the current question's.
 *
 * Both, because a pause that stopped only the session total would still be quietly inflating the
 * time recorded against whichever question happened to be on screen.
 */
function pause(state: SessionState, now: Now): SessionState {
  const stopped = leaveQuestion(state, now.monotonic);
  return {
    ...stopped,
    status: 'paused',
    clock: { ...stopped.clock, segments: closeSegment(stopped.clock.segments, now.monotonic) },
  };
}

function resume(state: SessionState, now: Now): SessionState {
  const started: SessionState = {
    ...state,
    status: 'active',
    pausedByUser: false,
    clock: { ...state.clock, segments: openSegment(state.clock.segments, now.monotonic) },
  };
  return enterQuestion(started, started.cursor, now.monotonic);
}

/**
 * A tick only notices expiry; it never adds time.
 *
 * Nothing depends on it having fired — the duration is the sum of the segments either way — so a
 * throttled or missed tick costs at most a late redraw.
 */
function tick(state: SessionState, now: Now, exam?: CanonicalExam): SessionState {
  if (!isLive(state) && state.status !== 'confirming-submit') {
    return state;
  }
  if (state.clock.expired || !hasExpired(state.clock, now.monotonic)) {
    return state;
  }

  const expired: SessionState = { ...state, clock: { ...state.clock, expired: true } };
  return state.policy.autoSubmitOnTimeUp ? submit(expired, now, exam) : expired;
}

/**
 * Ends the session and scores it.
 *
 * Scoring happens here, once, rather than continuously: a score that updates as you type would be
 * feedback, and whether feedback is allowed is a policy decision made elsewhere.
 */
function submit(state: SessionState, now: Now, exam?: CanonicalExam): SessionState {
  const stopped = leaveQuestion(state, now.monotonic);
  const scores: Record<string, ReturnType<typeof scoreQuestion>> = {};

  if (exam !== undefined) {
    for (const questionId of stopped.blueprint.questionOrder) {
      const question = questionAt(exam, questionId);
      if (question !== undefined) {
        scores[questionId] = scoreQuestion(question, stopped.entries[questionId]?.response ?? null);
      }
    }
  }

  return {
    ...stopped,
    status: 'completed',
    submittedAt: now.wall,
    clock: { ...stopped.clock, segments: closeSegment(stopped.clock.segments, now.monotonic) },
    scores,
  };
}

/** Total active time, for display and for the exported session. */
export function sessionDurationMs(state: SessionState, at: number): number {
  return elapsedMs(state.clock.segments, at);
}

export { currentQuestionId };
